import React, { useState, useCallback, useMemo } from "react";
import { UI_BODY_SM, colors2 } from "oolib";
import styled from "styled-components";
import TagsInputSingle from "@/components/inputs/DynamicTagsInputs/TagsInputSingle";
import { ReportLanding } from "@/components/analysisModules/reports/ReportLanding";
import { useGetProfile } from "@/utils/react-query-hooks/profile";

const REPORT_ID = "6992bad776304439b8e77640";

// 0-based widget indices where the volunteer tag ID should be injected
// into widgetFilters[0].values[0].value
const VOLUNTEER_FILTER_WIDGET_INDICES = [1, 7];
// Widget index 0: inject into props.selectedFieldValue (single-element array)
const VOLUNTEER_SELECTED_FIELD_WIDGET_INDEX = 0;
// Widget index 2: inject the volunteer's MAD centre(s) into
// widgetFilters[0].values. The saved filter is a nestedRollupTagType with
// rollupPath ["volunteers", "mADCentres"], so the backend resolves
// "all volunteers tagged with these MAD centres" and the card averages
// attendance over them. selectedFieldValue is removed — WidgetMetricCard
// falls back to all filter-scoped values, so the MAD centre avg is computed
// without injecting an explicit volunteer-id list.
// A volunteer with no MAD centre tag has nothing to scope by, so the widget
// is dropped for them rather than silently showing a national number under
// a "MAD Centre" title.
const MAD_CENTRE_FILTER_WIDGET_INDEX = 2;
// Widget index 3: national avg. selectedFieldValue cleared for the same
// fallback reason as widget 2.
const NATIONAL_AVG_WIDGET_INDEX = 3;
// The Child Progress custom widget takes the volunteer as its one input. It is
// found by what it is rather than where it sits, so it can be moved or
// re-added on the report without touching this file — unlike the index-based
// rules above, which is why new widgets must be APPENDED to that report.
const CHILD_PROGRESS_TEMP_WIDGET = "TempChildProgress";
const isChildProgressWidget = (widget) =>
  widget.comp === "WidgetTemp_SHI" && widget.props?.tempWidget === CHILD_PROGRESS_TEMP_WIDGET;

const StyledControls = styled.div`
  display: flex;
  gap: 1.6rem;
  align-items: flex-end;
  padding: 1.6rem 2rem;
  background: ${colors2.grey5};
  border-bottom: 1px solid ${colors2.grey10};
`;

// Shallow clone-on-write helpers — pass arrays by reference and only rebuild
// the small slice of the tree we actually mutate.
const replaceFirstFilterValue = (widget, value, display) => {
  const filter0 = widget.props?.widgetFilters?.[0];
  const val0 = filter0?.values?.[0];
  if (!val0) return widget;
  return {
    ...widget,
    props: {
      ...widget.props,
      widgetFilters: [
        {
          ...filter0,
          values: [
            { ...val0, value, ...(display ? { display } : {}) },
            ...filter0.values.slice(1),
          ],
        },
        ...widget.props.widgetFilters.slice(1),
      ],
    },
  };
};

// Replaces the ENTIRE values array of widgetFilters[0]. Used for the MAD
// centre filter because a volunteer can be tagged with more than one centre
// and the average should cover all of them.
const replaceFilterValues = (widget, values) => {
  const filter0 = widget.props?.widgetFilters?.[0];
  if (!filter0) return widget;
  return {
    ...widget,
    props: {
      ...widget.props,
      widgetFilters: [
        { ...filter0, values },
        ...widget.props.widgetFilters.slice(1),
      ],
    },
  };
};

const replaceProps = (widget, propsPatch) => ({
  ...widget,
  props: { ...widget.props, ...propsPatch },
});

// Widgets 2 (MAD centre avg) and 3 (national avg) used to receive a 5K-element
// selectedFieldValue array. Dropping the key makes the widget aggregate over
// every filter-scoped value automatically — same numeric result, no payload,
// no scan.
//
// The key is REMOVED rather than set to []: an absent key is the card's only
// unambiguous way to say "every value". An empty array reads that way today
// only because these widgets carry no fieldValuesVisibilityMode; configure one
// of them through the Show/Hide picker once and the same [] would start meaning
// "the user unchecked everything", blanking the card.
const clearSelectedFieldValue = (widget) => {
  if (!widget.props?.selectedFieldValue) return widget;
  const { selectedFieldValue, ...props } = widget.props;
  return { ...widget, props };
};

function makeConfigTransform({ volunteerId, volunteerDisplay, madCentreTags }) {
  if (!volunteerId) return undefined;

  const hasMadCentre = madCentreTags.length > 0;

  return (config) =>
    config.map((widget, idx) => {
      if (isChildProgressWidget(widget)) {
        return replaceProps(widget, {
          tempWidgetProps: {
            ...(widget.props.tempWidgetProps || {}),
            volunteer: { _id: volunteerId, ...(volunteerDisplay ? { display: volunteerDisplay } : {}) },
          },
        });
      }
      if (VOLUNTEER_FILTER_WIDGET_INDICES.includes(idx)) {
        return replaceFirstFilterValue(widget, volunteerId, volunteerDisplay);
      }
      if (idx === VOLUNTEER_SELECTED_FIELD_WIDGET_INDEX) {
        // Injected unconditionally. Guarding on the key existing used to work
        // only because it was saved as a non-empty array; the moment this widget
        // is re-saved through the value picker with everything selected the key
        // is DELETED (applyManualSelection), the guard goes false, no volunteer
        // id is injected — and the card silently averages every volunteer under
        // a "Volunteer Attendance – <name>" title.
        const patch = { selectedFieldValue: [volunteerId] };
        if (volunteerDisplay) {
          patch.metricTitle = `Volunteer Attendance – ${volunteerDisplay}`;
        }
        return replaceProps(widget, patch);
      }
      if (idx === MAD_CENTRE_FILTER_WIDGET_INDEX) {
        if (!hasMadCentre) return null; // dropped below
        const withMadCentreFilter = replaceFilterValues(
          widget,
          madCentreTags.map((t) => ({ value: t._id, display: t.display }))
        );
        return clearSelectedFieldValue(withMadCentreFilter);
      }
      if (idx === NATIONAL_AVG_WIDGET_INDEX) {
        return clearSelectedFieldValue(widget);
      }
      return widget;
    }).filter(Boolean);
}

export const VolunteerDashboard = ({ preset = null }) => {
  const [selectedVolunteer, setSelectedVolunteer] = useState(undefined);

  const volunteerId = preset?._id || selectedVolunteer?.data?.[0]?._id || null;
  const volunteerDisplay =
    preset?.display || selectedVolunteer?.data?.[0]?.display || null;

  const { data: profileRes, status: profileStatus } = useGetProfile(
    volunteerId,
    "volunteers",
    { enabled: !!volunteerId }
  );
  // All MAD centres the volunteer is tagged with (usually one, occasionally more).
  const madCentreTags = useMemo(
    () => profileRes?.data?.profile?.tags?.mADCentres?.data || [],
    [profileRes]
  );
  const madCentreKey = madCentreTags.map((t) => t._id).join(",");

  const configTransform = useCallback(
    (config) => {
      const transform = makeConfigTransform({
        volunteerId,
        volunteerDisplay,
        madCentreTags,
      });
      return transform ? transform(config) : config;
    },
    [volunteerId, volunteerDisplay, madCentreTags]
  );

  // Render once the profile fetch has settled (success OR error) so a
  // volunteer without a MAD centre tag still gets the rest of the dashboard.
  const ready = volunteerId && profileStatus !== "loading" && profileStatus !== "idle";

  return (
    <div>
      {!preset && (
        <StyledControls>
          <div style={{ flex: 1, maxWidth: "400px" }}>
            <UI_BODY_SM style={{ marginBottom: "0.4rem" }}>
              Select Volunteer
            </UI_BODY_SM>
            <TagsInputSingle
              id="volunteerSelector"
              tagType="volunteers"
              value={selectedVolunteer}
              onChange={(id, v) => setSelectedVolunteer(v)}
            />
          </div>
        </StyledControls>
      )}

      {ready ? (
        <ReportLanding
          key={`${volunteerId}:${madCentreKey}`}
          reportId={REPORT_ID}
          configTransform={configTransform}
          hideFilterSidebar
          hideBackButton
          forceReadOnly
        />
      ) : volunteerId ? (
        <div style={{ padding: "3rem 2rem" }}>
          <UI_BODY_SM style={{ color: colors2.grey40 }}>
            Loading volunteer profile...
          </UI_BODY_SM>
        </div>
      ) : (
        <div style={{ padding: "3rem 2rem" }}>
          <UI_BODY_SM style={{ color: colors2.grey40 }}>
            Select a volunteer above to render the dashboard.
          </UI_BODY_SM>
        </div>
      )}
    </div>
  );
};
